(() => {
  'use strict';

  const init = (root) => {
    if (!root || root.dataset.ufLayers712Ready === 'true') return;

    const designer = root.querySelector('[data-uf-designer]');
    if (!designer) return;
    root.dataset.ufLayers712Ready = 'true';

    const sideButtons = Array.from(designer.querySelectorAll('[data-uf-side]'));
    const layerButtons = Array.from(designer.querySelectorAll('[data-uf-layer]'));
    const panels = Array.from(designer.querySelectorAll('[data-uf-layer-panel]'));
    const canvas = designer.querySelector('[data-uf-designer-canvas]');
    const svgText = designer.querySelector('[data-uf-svg-text]');
    const svgArtwork = designer.querySelector('[data-uf-svg-artwork]');
    const svgQr = designer.querySelector('[data-uf-svg-qr]');
    const textInput = root.querySelector('[data-uf-engraving-text]');
    const artworkInput = root.querySelector('[data-uf-artwork-input]');
    const designJson = designer.querySelector('[data-uf-design-json]');
    const sidesProperty = designer.querySelector('[data-uf-property-sides]');
    const qrProperty = designer.querySelector('[data-uf-property-qr]');
    const qrInput = designer.querySelector('[data-uf-qr-content]');
    const artworkSize = designer.querySelector('[data-uf-artwork-size]');
    const artworkX = designer.querySelector('[data-uf-artwork-x]');
    const artworkY = designer.querySelector('[data-uf-artwork-y]');
    const artworkSizeOut = designer.querySelector('[data-uf-artwork-size-output]');
    const artworkXOut = designer.querySelector('[data-uf-artwork-x-output]');
    const artworkYOut = designer.querySelector('[data-uf-artwork-y-output]');
    const qrSize = designer.querySelector('[data-uf-qr-size]');
    const qrSizeOut = designer.querySelector('[data-uf-qr-size-output]');

    let activeSide = designer.dataset.ufSideLabel === 'back' ? 'back' : 'front';
    let activeLayer = 'text';
    let artworkReaderToken = 0;
    let draggingArtwork = false;
    let frameId = 0;
    let qrTimer = 0;
    let lastQrUrl = '';
    const artworkUndoCache = { front: '', back: '' };

    const blankArtwork = () => ({ url: '', fileName: '', size: 160, xPercent: 50, yPercent: 42 });
    const sideState = {
      front: { text: textInput?.value || '', qr: '', artwork: blankArtwork() },
      back: { text: '', qr: '', artwork: blankArtwork() }
    };

    const currentArtwork = () => sideState[activeSide].artwork;

    const scheduleMerge = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        mergeDesignData();
      });
    };

    const setImageHref = (node, value) => {
      if (!node) return;
      if (value) {
        node.setAttribute('href', value);
        node.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', value);
      } else {
        node.removeAttribute('href');
        node.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
      }
    };

    const updateArtwork = () => {
      if (!svgArtwork) return;
      const artwork = currentArtwork();
      const size = Number(artworkSize?.value || artwork.size || 160);
      const xPercent = Number(artworkX?.value || artwork.xPercent || 50);
      const yPercent = Number(artworkY?.value || artwork.yPercent || 42);
      artwork.size = size; artwork.xPercent = xPercent; artwork.yPercent = yPercent;
      setImageHref(svgArtwork, artwork.url);
      const height = Math.round(size * 0.75);
      const x = 58 + (484 * xPercent / 100) - size / 2;
      const y = 48 + (264 * yPercent / 100) - height / 2;

      svgArtwork.setAttribute('x', String(x));
      svgArtwork.setAttribute('y', String(y));
      svgArtwork.setAttribute('width', String(size));
      svgArtwork.setAttribute('height', String(height));
      svgArtwork.hidden = !artwork.url;

      if (artworkSizeOut) artworkSizeOut.textContent = String(size);
      if (artworkXOut) artworkXOut.textContent = String(xPercent);
      if (artworkYOut) artworkYOut.textContent = String(yPercent);
    };

    const buildQrUrl = (value, size) => {
      const px = Math.max(150, Math.min(600, size * 2));
      return `https://api.qrserver.com/v1/create-qr-code/?size=${px}x${px}&margin=0&data=${encodeURIComponent(value)}`;
    };

    const applyQrPreview = () => {
      if (!svgQr) return;
      const value = qrInput?.value.trim() || '';
      const size = Number(qrSize?.value || 110);
      sideState[activeSide].qr = value;

      if (qrSizeOut) qrSizeOut.textContent = String(size);
      if (qrProperty) qrProperty.value = value;

      if (!value) {
        svgQr.hidden = true;
        return;
      }

      const nextUrl = buildQrUrl(value, size);
      if (nextUrl !== lastQrUrl) {
        lastQrUrl = nextUrl;
        setImageHref(svgQr, nextUrl);
      }
      svgQr.setAttribute('width', String(size));
      svgQr.setAttribute('height', String(size));
      svgQr.setAttribute('x', String(300 - size / 2));
      svgQr.setAttribute('y', String(180 - size / 2));
      svgQr.hidden = false;
    };

    const scheduleQrPreview = () => {
      window.clearTimeout(qrTimer);
      qrTimer = window.setTimeout(() => {
        applyQrPreview();
        scheduleMerge();
      }, 300);
    };

    const updateLayerVisibility = () => {
      // All populated design objects remain visible on the product canvas.
      // The active layer controls only which editing panel is open.
      if (svgText) svgText.hidden = !(textInput?.value || '').trim();
      updateArtwork();
      applyQrPreview();

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.ufLayerPanel !== activeLayer;
      });
      layerButtons.forEach((button) => {
        const active = button.dataset.ufLayer === activeLayer;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    function mergeDesignData() {
      if (!designJson) return;
      let data = {};
      try {
        data = JSON.parse(designJson.value || '{}');
      } catch (_error) {
        data = {};
      }

      data.version = '6.0.2';
      data.activeSide = activeSide;
      data.activeLayer = activeLayer;
      data.sides = {
        front: { ...sideState.front },
        back: { ...sideState.back }
      };
      data.artwork = {
        front: { fileName: sideState.front.artwork.fileName, previewAvailable: Boolean(sideState.front.artwork.url), size: sideState.front.artwork.size, xPercent: sideState.front.artwork.xPercent, yPercent: sideState.front.artwork.yPercent },
        back: { fileName: sideState.back.artwork.fileName, previewAvailable: Boolean(sideState.back.artwork.url), size: sideState.back.artwork.size, xPercent: sideState.back.artwork.xPercent, yPercent: sideState.back.artwork.yPercent }
      };
      data.qr = {
        content: sideState[activeSide].qr,
        size: Number(qrSize?.value || 110)
      };

      designJson.value = JSON.stringify(data);
      if (sidesProperty) {
        sidesProperty.value = sideState.back.text.trim() || sideState.back.qr.trim() || sideState.back.artwork.url
          ? 'Front and back'
          : 'Front';
      }
    }

    const saveCurrentSide = () => {
      if (textInput) sideState[activeSide].text = textInput.value;
      if (qrInput) sideState[activeSide].qr = qrInput.value;
      const artwork = currentArtwork();
      if (artworkSize) artwork.size = Number(artworkSize.value || 160);
      if (artworkX) artwork.xPercent = Number(artworkX.value || 50);
      if (artworkY) artwork.yPercent = Number(artworkY.value || 42);
    };

    const loadSide = (side) => {
      if (!sideState[side] || side === activeSide) return;
      saveCurrentSide();
      activeSide = side;

      if (textInput) {
        textInput.value = sideState[side].text;
        textInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (qrInput) qrInput.value = sideState[side].qr;
      const artwork = sideState[side].artwork;
      if (artworkSize) artworkSize.value = String(artwork.size);
      if (artworkX) artworkX.value = String(artwork.xPercent);
      if (artworkY) artworkY.value = String(artwork.yPercent);
      setImageHref(svgArtwork, artwork.url);
      if (canvas) canvas.classList.toggle('is-back', side === 'back');

      sideButtons.forEach((button) => {
        const active = button.dataset.ufSide === side;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });

      lastQrUrl = '';
      updateLayerVisibility();
      scheduleMerge();
    };

    sideButtons.forEach((button) => {
      button.addEventListener('click', () => loadSide(button.dataset.ufSide || 'front'));
    });

    layerButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeLayer = button.dataset.ufLayer || 'text';
        updateLayerVisibility();
        scheduleMerge();
      });
    });

    // Direct canvas selection opens the matching controls automatically.
    designer.addEventListener('uf:canvas-object-selected', (event) => {
      const type = event.detail?.type;
      if (!['text', 'artwork', 'qr'].includes(type)) return;
      activeLayer = type;
      updateLayerVisibility();
      scheduleMerge();
    });

    // Allow choosing the same file again after a refresh or a cancelled picker.
    artworkInput?.addEventListener('click', () => {
      artworkInput.value = '';
    });

    artworkInput?.addEventListener('change', () => {
      const file = artworkInput.files?.[0];
      const token = ++artworkReaderToken;
      const artwork = currentArtwork();
      artwork.url = ''; artwork.fileName = '';
      setImageHref(svgArtwork, '');

      if (!file || !/image\/(png|jpeg|webp|svg\+xml)/i.test(file.type)) {
        updateLayerVisibility();
        scheduleMerge();
        designer.dispatchEvent(new CustomEvent('uf:artwork-state-change', {
          bubbles: true,
          detail: { hasArtwork: false, side: activeSide }
        }));
        return;
      }

      // A data URL is more reliable than a blob URL inside inline SVG across
      // Safari, Chrome and Shopify theme-preview iframes.
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (token !== artworkReaderToken || typeof reader.result !== 'string') return;
        artwork.url = reader.result; artwork.fileName = file.name || '';
        artworkUndoCache[activeSide] = artwork.url;
        setImageHref(svgArtwork, artwork.url);
        activeLayer = 'artwork';
        updateLayerVisibility();
        scheduleMerge();
        designer.dispatchEvent(new CustomEvent('uf:artwork-state-change', {
          bubbles: true,
          detail: { hasArtwork: true, side: activeSide }
        }));
      });
      reader.addEventListener('error', () => {
        if (token !== artworkReaderToken) return;
        artwork.url = ''; artwork.fileName = '';
        artworkUndoCache[activeSide] = '';
        setImageHref(svgArtwork, '');
        updateLayerVisibility();
        designer.dispatchEvent(new CustomEvent('uf:artwork-state-change', {
          bubbles: true,
          detail: { hasArtwork: false, side: activeSide }
        }));
      });
      reader.readAsDataURL(file);
    });

    [artworkSize, artworkX, artworkY].forEach((control) => {
      control?.addEventListener('input', () => {
        updateArtwork();
        scheduleMerge();
      });
    });

    designer.querySelectorAll('[data-uf-artwork-align]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!artworkX) return;
        const value = button.dataset.ufArtworkAlign;
        artworkX.value = value === 'left' ? '25' : value === 'right' ? '75' : '50';
        updateArtwork();
        scheduleMerge();
      });
    });

    designer.querySelectorAll('[data-uf-artwork-nudge]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!artworkX || !artworkY) return;
        const direction = button.dataset.ufArtworkNudge;
        const step = 2;
        if (direction === 'left') artworkX.value = String(Math.max(Number(artworkX.min), Number(artworkX.value) - step));
        if (direction === 'right') artworkX.value = String(Math.min(Number(artworkX.max), Number(artworkX.value) + step));
        if (direction === 'up') artworkY.value = String(Math.max(Number(artworkY.min), Number(artworkY.value) - step));
        if (direction === 'down') artworkY.value = String(Math.min(Number(artworkY.max), Number(artworkY.value) + step));
        updateArtwork();
        scheduleMerge();
      });
    });

    const svg = svgArtwork?.ownerSVGElement;
    const setArtworkFromPointer = (event) => {
      if (!svg || !artworkX || !artworkY) return;
      const rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const viewX = (event.clientX - rect.left) * 600 / rect.width;
      const viewY = (event.clientY - rect.top) * 360 / rect.height;
      artworkX.value = String(Math.max(10, Math.min(90, ((viewX - 58) / 484) * 100)));
      artworkY.value = String(Math.max(15, Math.min(85, ((viewY - 48) / 264) * 100)));
      updateArtwork();
      scheduleMerge();
    };

    svgArtwork?.addEventListener('pointerdown', (event) => {
      if (!currentArtwork().url) return;
      draggingArtwork = true;
      svgArtwork.setPointerCapture?.(event.pointerId);
      setArtworkFromPointer(event);
      event.preventDefault();
    });
    svgArtwork?.addEventListener('pointermove', (event) => {
      if (!draggingArtwork) return;
      setArtworkFromPointer(event);
      event.preventDefault();
    });
    const stopArtworkDrag = (event) => {
      draggingArtwork = false;
      svgArtwork?.releasePointerCapture?.(event.pointerId);
    };
    svgArtwork?.addEventListener('pointerup', stopArtworkDrag);
    svgArtwork?.addEventListener('pointercancel', stopArtworkDrag);

    designer.addEventListener('uf:full-reset', () => {
      artworkReaderToken += 1;
      draggingArtwork = false;
      sideState.front = { text: '', qr: '', artwork: blankArtwork() };
      sideState.back = { text: '', qr: '', artwork: blankArtwork() };
      artworkUndoCache.front = '';
      artworkUndoCache.back = '';
      if (artworkInput) artworkInput.value = '';
      const uploadLabel = root.querySelector('[data-uf-upload-file]');
      if (uploadLabel) uploadLabel.textContent = 'No file selected';
      if (qrInput) qrInput.value = '';
      if (artworkSize) artworkSize.value = '160';
      if (artworkX) artworkX.value = '50';
      if (artworkY) artworkY.value = '42';
      if (qrSize) qrSize.value = '110';
      lastQrUrl = '';
      setImageHref(svgArtwork, '');
      setImageHref(svgQr, '');
      if (svgArtwork) svgArtwork.hidden = true;
      if (svgQr) svgQr.hidden = true;
      updateLayerVisibility();
      mergeDesignData();
    });

    designer.addEventListener('uf:canvas-history-restored', (event) => {
      if (event.detail?.side && event.detail.side !== activeSide) return;
      const shouldShow = Boolean(event.detail?.artworkVisible);
      const artwork = currentArtwork();
      if (shouldShow) {
        artwork.url = artwork.url || artworkUndoCache[activeSide];
      } else if (artwork.url) {
        artworkUndoCache[activeSide] = artwork.url;
        artwork.url = '';
      }
      setImageHref(svgArtwork, artwork.url);
      updateArtwork();
      scheduleMerge();
    });

    qrInput?.addEventListener('input', () => {
      sideState[activeSide].qr = qrInput.value;
      scheduleQrPreview();
    });
    qrSize?.addEventListener('input', scheduleQrPreview);

    textInput?.addEventListener('input', () => {
      sideState[activeSide].text = textInput.value;
      scheduleMerge();
    });

    root.closest('form')?.addEventListener('submit', () => {
      saveCurrentSide();
      mergeDesignData();
    });

    window.addEventListener('pagehide', () => {
      window.clearTimeout(qrTimer);
      if (frameId) window.cancelAnimationFrame(frameId);
    }, { once: true });

    updateLayerVisibility();
    mergeDesignData();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-uf-personalization-side]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
